// The league mock, together (MOCK_DRAFT.md §6.2, scenario C10).
//
// THE LEAGUE'S OPEN MOCK, FOUND FIRST AND THEN JOINED. A socket a room
// refuses cannot say why, so the page asks which mock is open before it opens
// one, and keeps that mock once it has it: the read asked again after the
// last pick says none is open, and the board must stay on screen. Opening one
// is the mock page's (`MockDraftScreen`); the chat's card and the mock page's
// Join both land here.

import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useLeagueBase } from '../data/hooks';
import { mockStillOpen, useLeagueMock } from '../data/leagueMock';
import { useLeagueSource } from '../data/leagueSource';
import { useLiveDraft } from '../data/liveDraft';
import { EmptyState } from '../kit';
import { Panel } from '../kit/Panel';
import { Button, Line, Stack, Text, tell } from '../kit/ui';
import { DraftRoom } from './draft/DraftRoom';
import { deviceNow, socketActions, useTicking } from './DraftRoomScreen';
import '../styles/screens/draft-room.css';

export function LeagueMockScreen() {
  const source = useLeagueSource();
  if (source !== 'api') {
    return (
      <EmptyState>
        A mock with the league runs in your own leagues. The practice league has its own draft room
        to try.
      </EmptyState>
    );
  }
  return <FindTheMock />;
}

function FindTheMock() {
  const { leagueId } = useParams();
  const base = useLeagueBase();
  const { snapshot, reload } = useLeagueMock(leagueId ?? null);
  const open = snapshot.state === 'ready' ? (snapshot.value?.open ?? null) : null;
  const [mockId, setMockId] = useState<string | null>(null);
  useEffect(() => {
    if (mockId === null && open !== null) setMockId(open.id);
  }, [mockId, open]);

  if (mockId !== null && leagueId) {
    return <TogetherRoom leagueId={leagueId} mockId={mockId} base={base} />;
  }
  if (snapshot.state === 'failed') {
    return (
      <Nothing title="Mock with the league" base={base}>
        <Text tone="secondary" block>
          {snapshot.failure.message}
        </Text>
        <Line>
          <Button onClick={reload}>Try again</Button>
        </Line>
      </Nothing>
    );
  }
  if (snapshot.state !== 'ready' || open !== null) {
    return <EmptyState>Finding the league&rsquo;s mock…</EmptyState>;
  }
  return (
    <Nothing title="Mock with the league" base={base}>
      <Text block>No mock with the league is open right now.</Text>
      <Text size="sm" tone="secondary" block>
        Anybody in the league can open one from the mock draft page. A finished one is kept in each
        drafter&rsquo;s mocks.
      </Text>
    </Nothing>
  );
}

/** A page with one panel and the way back to the mock page. */
function Nothing({ title, base, children }: { title: string; base: string; children: ReactNode }) {
  return (
    <div className="fsp-screen">
      <div className="fsp-widgets fsp-predraft-column">
        <div className="fsp-widget-column">
          <Panel title={title}>
            <Stack gap={3}>
              {children}
              <Line>
                <Button to={`${base}/draft/mock`} variant="primary">
                  Mock draft
                </Button>
                <Button to={`${base}/draft`} variant="quiet">
                  Back to the draft
                </Button>
              </Line>
            </Stack>
          </Panel>
        </div>
      </div>
    </div>
  );
}

/** The mock's room, over its socket. */
function TogetherRoom({
  leagueId,
  mockId,
  base,
}: {
  leagueId: string;
  mockId: string;
  base: string;
}) {
  const live = useLiveDraft(
    leagueId,
    (refusal) => tell(refusal.message, 'bad'),
    deviceNow,
    mockId,
    () => mockStillOpen(leagueId, mockId),
  );
  const now = useTicking() + live.offset;
  if (live.connection === 'closed' && live.view?.status !== 'complete') {
    // GONE WHILE THIS PAGE WAS AWAY: a lobby nobody started, or a mock that
    // finished without this socket in it, which kept every seat all the same.
    const started = live.view !== null && live.view.status !== 'lobby';
    return (
      <Nothing title="Mock with the league" base={base}>
        <Text block>
          {started
            ? 'This mock finished while you were away.'
            : 'Nobody started this mock, so it closed.'}
        </Text>
        <Text size="sm" tone="secondary" block>
          {started
            ? 'Each seat is kept in its drafter’s mocks, on the mock draft page.'
            : 'Anybody in the league can open another from the mock draft page.'}
        </Text>
      </Nothing>
    );
  }
  if (live.view === null) {
    return (
      <EmptyState>
        {live.connection === 'signed-out'
          ? 'Your session ended. Sign in again to come back to the room.'
          : live.connection === 'offline'
            ? 'The mock room cannot be reached right now. This page keeps trying.'
            : 'Joining the mock…'}
      </EmptyState>
    );
  }
  return (
    <DraftRoom
      view={live.view}
      now={now}
      connection={live.connection}
      actions={socketActions(live.send)}
      recapTo={`${base}/draft/mock`}
      teamTo={`${base}/draft/mock`}
    />
  );
}
